import {
  blueDark,
  crimsonDark,
  cyanDark,
  goldDark,
  grassDark,
  grayDark,
  indigoDark,
  limeDark,
  plumDark,
  purpleDark,
  redDark,
  tealDark,
  tomatoDark,
  yellowDark,
} from '@radix-ui/colors';

const background = {
  '--background-default': '#0D172D',
  '--background-soft': '#060F27',
  '--background-backdrop': 'rgba(4, 13, 38, 0.24)',
  '--background-overlay':
    'linear-gradient(180deg, rgba(13, 23, 45, 1) 0%, rgba(13, 23, 45, 0.85) 100%)',
  '--background-gradient':
    'linear-gradient(180deg, rgba(4, 13, 38, 0.00) 0%, rgba(4, 13, 38, 0.24) 100%)',
};

const surface = {
  '--surface-soft': '#1D263B',
  '--surface-default': '#1D263B',
  '--surface-hover': '#31384D',
  '--surface-pressed': '#31384D',
  '--surface-active': '#31384D',
  '--surface-inverse': '#FFFFFF',
  '--surface-primary': '#788DFF',
  '--surface-success': '#002B0D',
  '--surface-warning': '#2E2200',
  '--surface-critical': '#50000D',
  '--surface-neutral': '#1B2337',
  '--surface-white': '#FFFFFF',

  '--surface-decorative-indigo-default': indigoDark.indigo3,
  '--surface-decorative-indigo-hover': indigoDark.indigo4,
  '--surface-decorative-indigo-active': indigoDark.indigo5,

  '--surface-decorative-teal-default': tealDark.teal3,
  '--surface-decorative-teal-hover': tealDark.teal4,
  '--surface-decorative-teal-active': tealDark.teal5,

  '--surface-decorative-cyan-default': cyanDark.cyan3,
  '--surface-decorative-cyan-hover': cyanDark.cyan4,
  '--surface-decorative-cyan-active': cyanDark.cyan5,

  '--surface-decorative-purple-default': purpleDark.purple3,
  '--surface-decorative-purple-hover': purpleDark.purple4,
  '--surface-decorative-purple-active': purpleDark.purple5,

  '--surface-decorative-lime-default': limeDark.lime3,
  '--surface-decorative-lime-hover': limeDark.lime4,
  '--surface-decorative-lime-active': limeDark.lime5,

  '--surface-decorative-gold-default': goldDark.gold3,
  '--surface-decorative-gold-hover': goldDark.gold4,
  '--surface-decorative-gold-active': goldDark.gold5,
};

const text = {
  '--text-default': '#FFFFFF',
  '--text-soft': '#CFD7F1',
  '--text-disabled': '#4E5467',
  '--text-inverse': '#0D172D',
  '--text-primary': '#D0D5FC',
  '--text-success': '#48C265',
  '--text-warning': '#D1A600',
  '--text-critical': '#FF888B',
  '--text-critical-dark-red': redDark.red11,
  '--text-on-primary': '#FFFFFF',
  '--text-on-critical': '#FFFFFF',
  '--text-link': '#FFFFFF',

  '--text-decorative-indigo': indigoDark.indigo11,
  '--text-decorative-teal': tealDark.teal11,
  '--text-decorative-cyan': cyanDark.cyan11,
  '--text-decorative-purple': purpleDark.purple11,
  '--text-decorative-lime': limeDark.lime10,
  '--text-decorative-gold': goldDark.gold10,
};

const icon = {
  '--icon-default': '#FFFFFF',
  '--icon-soft': '#CFD7F1',
  '--icon-disabled': '#4E5467',
  '--icon-inverse': '#0D172D',
  '--icon-primary': '#D0D5FC',
  '--icon-success': '#48C265',
  '--icon-success-grass': grassDark.grass9,
  '--icon-warning': '#D1A600',
  '--icon-critical': '#FF888B',
  '--icon-critical-red': redDark.red9,
  '--icon-on-primary': '#FFFFFF',
  '--icon-on-critical': '#FFFFFF',
  '--icon-decorative-indigo': indigoDark.indigo11,
  '--icon-decorative-teal': tealDark.teal11,
  '--icon-decorative-cyan': cyanDark.cyan11,
  '--icon-decorative-purple': purpleDark.purple11,
  '--icon-decorative-lime': limeDark.lime10,
  '--icon-decorative-crimson': crimsonDark.crimson9,
  '--icon-success-lime': limeDark.lime9,
  '--icon-decorative-blue': '#3E63DD',
  '--icon-decorative-gold': goldDark.gold10,
};

const border = {
  '--border-default': '#272F44',
  '--border-soft': '#1D263B',
  '--border-hover': '#373E52',
  '--border-disabled': 'transparent',
  '--border-primary': '#788DFF',
  '--border-success': '#2EAB52',
  '--border-warning': '#FBD364',
  '--border-critical': '#FF5F69',
  '--border-focus': '#5875FF',
  '--border-decorative-indigo': indigoDark.indigo11,
  '--border-decorative-teal': tealDark.teal11,
  '--border-decorative-cyan': cyanDark.cyan11,
  '--border-decorative-purple': purpleDark.purple11,
  '--border-decorative-lime': limeDark.lime10,
  '--border-decorative-gold': goldDark.gold9,
};

const action = {
  '--action-primary-default': '#788DFF',
  '--action-primary-hover': '#95A6FF',
  '--action-primary-disabled': '#283044',
  '--action-primary-soft-hover': '#000FA3',
  '--action-critical-default': '#FF5F69',
  '--action-critical-hover': '#FF888B',
  '--action-critical-disabled': '#283044',
  '--action-critical-soft-hover': '#680014',
};

const grassDark11Rgb = '99, 193, 116';
const tomatoDark11Rgb = '241, 106, 80';
const blueDark11Rgb = '82, 169, 255';
const vizBullishDarkRgb = '72, 194, 101';
const vizBearishDarkRgb = '255, 136, 139';

const visual = {
  '--viz-bullish': '#48C265',
  '--viz-bullish-rgb': vizBullishDarkRgb,
  '--viz-bullish-soft': '#002B0D',
  '--viz-bearish': '#FF888B',
  '--viz-bearish-rgb': vizBearishDarkRgb,
  '--viz-bearish-soft': '#50000D',
  '--viz-neutral-soft': '#1B2337',
};

const elevation = {
  '--elevation-subtle':
    '0px 1px 2px rgba(0, 0, 0, 0.3), 0px 2px 6px 2px rgba(0, 0, 0, 0.15)',
  '--elevation-moderate':
    '0px 2px 3px rgba(0, 0, 0, 0.3), 0px 6px 10px 4px rgba(0, 0, 0, 0.15)',
  '--elevation-strong':
    '0px 4px 4px rgba(0, 0, 0, 0.3), 0px 8px 12px 6px rgba(0, 0, 0, 0.15)',
};

const lseg = {
  '--lseg-surface-on-layer-1-moderate': 'rgba(96, 104, 127, 0.4)',
  '--lseg-surface-on-layer-1-bold': 'rgba(96, 104, 127, 0.64)',
};

const custom = {
  '--background-default-rgb': '13, 23, 45',
  '--background-soft-rgb': '6, 15, 39',
  '--background-table-zebra': grayDark.gray1,
  '--chart-pane-gradient': 'hsl(0, 0%, 6%)',
  '--chart-multi-lines-hex-1': '#A5C1FF',
  '--chart-multi-lines-hex-2': '#FDAF89',
  '--chart-multi-lines-hex-3': '#E4ADF7',
  '--chart-multi-lines-hex-4': '#81CCE4',
  '--chart-multi-lines-hex-5': '#FFA9B8',
  '--chart-multi-lines-hex-6': '#F0B918',
  '--chart-multi-lines-hex-7': '#4ED5C1',
  '--chart-multi-lines-hex-8': '#FFA7C9',
  '--chart-multi-lines-hex-9': grassDark.grass10,
  '--chart-multi-lines-hex-10': yellowDark.yellow10,
  '--chart-multi-lines-hex-11': blueDark.blue10,
  '--chart-multi-lines-hex-12': indigoDark.indigo10,
  '--chart-multi-lines-hex-13': plumDark.plum10,
};

const kg = {
  '--kg-theme': indigoDark.indigo11,
  '--kg-parent': indigoDark.indigo9,
  '--kg-brand': tealDark.teal11,
};

const darkDefault = {
  ...background,
  ...surface,
  ...border,
  ...text,
  ...icon,
  ...action,
  ...visual,
  ...elevation,
  ...lseg,
  ...custom,
  ...kg,
};

const darkInverted = {
  ...darkDefault,
  '--viz-bullish': tomatoDark.tomato11,
  '--viz-bullish-rgb': tomatoDark11Rgb,
  '--viz-bullish-soft': tomatoDark.tomato4,
  '--viz-bearish': grassDark.grass11,
  '--viz-bearish-rgb': grassDark11Rgb,
  '--viz-bearish-soft': grassDark.grass4,
};
const darkAsian = {
  ...darkDefault,
  '--viz-bullish': tomatoDark.tomato11,
  '--viz-bullish-rgb': tomatoDark11Rgb,
  '--viz-bullish-soft': tomatoDark.tomato4,
  '--viz-bearish': blueDark.blue11,
  '--viz-bearish-rgb': blueDark11Rgb,
  '--viz-bearish-soft': blueDark.blue4,
};

const darkProtanopia = {
  ...darkDefault,
  '--viz-bearish': blueDark.blue11,
  '--viz-bearish-rgb': blueDark11Rgb,
  '--viz-bearish-soft': blueDark.blue4,
};

const darkDeuteranopia = {
  ...darkDefault,
  '--viz-bullish': blueDark.blue11,
  '--viz-bullish-rgb': blueDark11Rgb,
  '--viz-bullish-soft': blueDark.blue4,
};

export const darkTheme = {
  dark_default: darkDefault,
  dark_inverted: darkInverted,
  dark_asian: darkAsian,
  dark_protanopia: darkProtanopia,
  dark_deuteranopia: darkDeuteranopia,
};
